import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { hasLink } from '@ngxp/rest';
import { apiRootSelector, ApiRootLinkRel } from '@schir-int-client/api-root';
import { isNil } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, share, tap } from 'rxjs/operators';
import {
	askToDeleteUSignalAction,
	clearUSignalErrorAction,
	loadAdminRootAction,
	loadUSignaleAction,
	uploadUSignaleAction,
} from './admin.actions';
import {
	AdminRootResource,
	UnprocessableEntityError,
	UnterscheidungssignalListResource,
	UnterscheidungssignalResource,
} from './admin.model';
import { AdminState } from './admin.reducer';
import {
	adminRootSelector,
	freieUnterscheidungssignaleCountSelector,
	unterscheidungssignaleSelector,
	usignalErrorSelector,
} from './admin.selectors';
import { doIfLoadingRequired, StateResource } from '@schir-int-client/ngrx-helpers';

@Injectable({ providedIn: 'root' })
export class AdminFacade {
	constructor(private store: Store<AdminState>) {}

	adminRoot: Observable<AdminRootResource> = this.getAdminRoot();

	private getAdminRoot(): Observable<AdminRootResource> {
		return combineLatest([this.store.select(apiRootSelector), this.store.select(adminRootSelector)]).pipe(
			tap(([apiRoot, adminRoot]) => {
				if (isNil(adminRoot) && !isNil(apiRoot) && hasLink(apiRoot, ApiRootLinkRel.ADMIN)) {
					this.store.dispatch(loadAdminRootAction());
				}
			}),
			share(),
			map(([, adminRoot]) => (adminRoot ? adminRoot : null))
		);
	}

	readonly unterscheidungssignale: Observable<StateResource<UnterscheidungssignalListResource>> = this.store
		.select(unterscheidungssignaleSelector)
		.pipe(filter(resource => !doIfLoadingRequired(resource, () => this.store.dispatch(loadUSignaleAction()))));

	readonly freieUnterscheidungssignaleCount: Observable<number> = this.store.select(
		freieUnterscheidungssignaleCountSelector
	);

	readonly usignalError: Observable<UnprocessableEntityError> = this.store.select(usignalErrorSelector);

	uploadUnterscheidungssignale(file: File): void {
		this.store.dispatch(uploadUSignaleAction({ file }));
	}

	loadUnterscheidungssignale() {
		this.store.dispatch(loadUSignaleAction());
	}

	clearErrorMsg() {
		this.store.dispatch(clearUSignalErrorAction());
	}

	deleteUnterscheidungssignal(uSignal: UnterscheidungssignalResource) {
		this.store.dispatch(askToDeleteUSignalAction({ uSignal }));
	}
}
